import { PropsWithChildren, useCallback, useEffect, useMemo } from "react";
import useUser from "@hooks/user.hook";
import useYearsQuery from "@services/api/years/year.api";
import { Year } from "@services/api/years/year.types";
import useParameters from "@src/app/parameters/useParameter";
import useYearStore from "@src/providers/year.store";

export default function YearProvider({ children }: PropsWithChildren) {
  const { setVisibleYears } = useYearStore();

  const { isAdmin } = useUser();
  const { useYearPageQuery } = useYearsQuery();
  const { data: yearData, isLoading: isYearDataLoading } = useYearPageQuery({
    variables: {
      sort: ["active,desc", "startDate,desc", "endDate,desc"],
      size: 9999
    }
  });

  const parameters = useParameters();
  const isVisibleForCurrentUser = useCallback(
    (year: Year) => {
      if (isAdmin) {
        return true;
      }

      const visibleYears = (parameters["visible.years"] as string[]) || [];
      if (!visibleYears.length) {
        return true;
      }
      return visibleYears.includes(year.externalId);
    },
    [isAdmin, parameters]
  );

  const filteredYears = useMemo(() => {
    if (!yearData) {
      return [];
    }
    const visibleYears = yearData.content?.filter(isVisibleForCurrentUser);
    if (!visibleYears?.length) {
      return [];
    }
    return visibleYears;
  }, [yearData, isVisibleForCurrentUser]);

  useEffect(() => {
    setVisibleYears(filteredYears);
  }, [setVisibleYears, filteredYears]);

  if (isYearDataLoading) {
    return null;
  }

  return <>{children}</>;
}
